import { useCallback, useMemo, useState } from 'react';

import { Box, Button, CircularProgress, MenuItem, MenuList, Typography, useTheme } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';
import type { GridColDef } from '@mui/x-data-grid';
import { IoClose } from 'react-icons/io5';
import { TbPencil } from 'react-icons/tb';
import { HiOutlineEye } from 'react-icons/hi';
import { FiMessageCircle } from 'react-icons/fi';

import { Shift, TimeAndAttendanceLocation } from '@vyce/core/src/types';
import {
  AppDataGridWithSavedPage,
  AppLink,
  AppSwitch,
  ConfirmDialog,
  GridActions,
} from '@vyce/core/src/components';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { TIME_INTERFACE_PERMISSIONS } from '@vyce/core/src/types';

import { monochrome } from '../../../theme/styles';
import { Props } from './types';
import { useData } from './hooks/useData';
import { formatDate } from '../../../utils';
import { buildNameIdUrlSegment } from '../../../utils/url';

export const LocationsAndShifts = (props: Props) => {
  const theme = useTheme();
  const textColor = theme.palette.type === 'dark' ? monochrome.medium : monochrome.mediumdark;
  const { can, canOnResource } = usePermissions();

  const canUpdateSite = useCallback(
    (siteId: string) => canOnResource(TIME_INTERFACE_PERMISSIONS.UPDATE_SITE, 'location', siteId),
    [canOnResource]
  );

  const canRemoveSite = useCallback(
    (siteId: string) => canOnResource(TIME_INTERFACE_PERMISSIONS.REMOVE_SITES, 'location', siteId),
    [canOnResource]
  );

  // Preview and the location chat come with module access, so a row always has
  // at least those two — but count anyway, so an empty menu can't render.
  const menuItemCount = useCallback(
    (siteId: string) => 2 + (canUpdateSite(siteId) ? 1 : 0) + (canRemoveSite(siteId) ? 1 : 0),
    [canUpdateSite, canRemoveSite]
  );

  const {
    isClosed,
    loading,
    closeGridAction,
    isMobile,
    handleSearchChange,
    locations,
    total,
    sortModel,
    openCloseDialog,
    isForbidDIalogOpen,
    closeForbidDialog,
    closeLocation,
    setOpenCloseDialog,
    setSelectedLocation,
    setOffset,
    handleSortModelChange,
    handlePageChange,
    handleActiveStatusChange,
    goToCreateLocationPage,
    goToLocation,
    handleCloseLocationClick,
    goToLocationChat,
  } = useData(props);
  const [getChatLoading, setGetChatLoading] = useState(false);

  const handleGoToLocationChat = async (location: TimeAndAttendanceLocation) => {
    setGetChatLoading(true);
    await goToLocationChat(location);
    setGetChatLoading(false);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Location Name',
        flex: 1,
        disableColumnMenu: true,
        minWidth: 150,
        renderCell: params => (
          <Box display="flex" width="100%">
            {!isClosed && !loading && (
              <AppLink
                to={`/time/locations-and-shifts/${buildNameIdUrlSegment(
                  params.row.name,
                  params.row.uuid
                )}?mode=preview`}>
                {params.row?.name}
              </AppLink>
            )}
            {isClosed && !loading && <Typography variant="subtitle2">{params.row?.name}</Typography>}
          </Box>
        ),
      },
      {
        field: 'shifts',
        headerName: 'Shifts',
        flex: 0.9,
        minWidth: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Box display="flex" flexDirection="column" height={64} justifyContent="center">
            {params.row.shifts?.map((shift: Shift, index: number) => (
              <Typography style={{ fontWeight: 500 }} variant="caption" key={shift.name + index}>
                {shift.name}
              </Typography>
            ))}
          </Box>
        ),
      },
      {
        field: 'created_at',
        headerName: 'Creation date',
        flex: 0.9,
        minWidth: 150,
        disableColumnMenu: true,
        valueGetter: params => formatDate(params.row.created_at, 'DD/MM/YYYY'),
      },
      {
        field: '',
        headerName: '',
        width: 80,
        hideSortIcons: true,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Box display="flex" width="100%">
            {/* Rights are per location: the same person may edit one site and
                not the next, so every item is keyed on this row's own uuid. */}
            {!isClosed && menuItemCount(params.row.uuid) > 0 && (
              <GridActions close={closeGridAction}>
                <MenuList>
                  <MenuItem onClick={() => goToLocation(params.row as TimeAndAttendanceLocation, 'preview')}>
                    <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                      <HiOutlineEye size={14} /> Preview
                    </Box>
                  </MenuItem>
                  {canUpdateSite(params.row.uuid) && (
                    <MenuItem onClick={() => goToLocation(params.row as TimeAndAttendanceLocation, 'edit')}>
                      <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                        <TbPencil size={14} /> Edit
                      </Box>
                    </MenuItem>
                  )}
                  <MenuItem
                    disabled={getChatLoading}
                    onClick={() => handleGoToLocationChat(params.row as TimeAndAttendanceLocation)}>
                    <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                      {getChatLoading ? <CircularProgress size={14} /> : <FiMessageCircle size={14} />}
                      Go to location chat
                    </Box>
                  </MenuItem>
                  {canRemoveSite(params.row.uuid) && (
                    <MenuItem
                      onClick={() => handleCloseLocationClick(params.row as TimeAndAttendanceLocation)}>
                      <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                        <IoClose size={14} /> Close location
                      </Box>
                    </MenuItem>
                  )}
                </MenuList>
              </GridActions>
            )}
          </Box>
        ),
      },
    ],
    [closeGridAction, isClosed, loading, getChatLoading, canUpdateSite, canRemoveSite, menuItemCount]
  );

  return (
    <>
      <Box display="flex" gridGap={16} marginBottom={2} width="100%">
        <AppSearchInput
          onChange={handleSearchChange}
          isSmall
          fullWidth
          smallRounding
          flex={1}
          expanded={!isMobile}
        />

        <AppSwitch
          checked={isClosed}
          onChange={handleActiveStatusChange}
          leftOption={{
            content: <Typography style={{ fontSize: 14, fontWeight: 600 }}>Active</Typography>,
          }}
          rightOption={{
            content: <Typography style={{ fontSize: 14, fontWeight: 600 }}>Closed</Typography>,
          }}
        />

        {/*<FilterSystem filtersSections={[]} onFiltersChange={noop} />*/}

        {can(TIME_INTERFACE_PERMISSIONS.CREATE_SITES) && (
          <Button
            onClick={goToCreateLocationPage}
            variant="contained"
            size="small"
            startIcon={<AiOutlinePlus />}
            color="primary">
            Add new Location
          </Button>
        )}
      </Box>

      <AppDataGridWithSavedPage
        rows={locations || []}
        getRowId={row => row.uuid}
        columns={columns}
        noPaper
        rowHeight={100}
        height="calc(100vh - 240px)"
        loading={loading}
        rowCount={total}
        pageSize={GRID_PAGE_SIZE}
        rowsPerPageOptions={[GRID_PAGE_SIZE]}
        onSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        disableSelectionOnClick
        setOffset={setOffset}
      />

      <ConfirmDialog
        handleClose={() => {
          setOpenCloseDialog(false);
          setSelectedLocation(null);
        }}
        open={openCloseDialog}
        confirmText="Close Location"
        cancelText="Cancel"
        title="Do you want to close this Location?"
        subtitle="This means that no-one will be able to clock in or clock out of this location going forward."
        handleConfirm={() => closeLocation()}
      />

      {/* TODO: implement when Delete Location Completely api will be done */}
      {/* <ConfirmDialog
        handleClose={() => {
          closeDeleteDialog();
          setSelectedLocation(null);
        }}
        open={isDeleteDialogOpen}
        confirmText="Delete Location"
        cancelText="Cancel"
        title="Do you want to delete this Location?"
        subtitle="This action cannot be undone. All the workers assign to this location will be notified."
        handleConfirm={() => deleteLocation()}
      /> */}

      <ConfirmDialog
        handleClose={() => {
          closeForbidDialog();
          setSelectedLocation(null);
        }}
        open={isForbidDIalogOpen}
        cancelText="Ok, close"
        title="You can't delete this location"
        subtitle="Workers are still checked in, wait until the end of the day."
      />
    </>
  );
};
